import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import salaryService from '../../services/SalaryService.js';
import axiosInstance from '../../config/axios.js';
import { Wallet, Plus, Eye, Pencil, Download } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const SalaryList = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgentOrDriver = currentUser.role_name === 'Agent' || currentUser.role === 'Agent';
  const isSystemAdminOrAdmin =
    currentUser.role_name === 'System Admin' ||
    currentUser.role === 'System Admin' ||
    currentUser.role_name === 'Admin' ||
    currentUser.role === 'Admin' ||
    currentUser.role_name === 'Accountant' ||
    currentUser.role === 'Accountant';
  const [salaries, setSalaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    user_id: '',
    status: '',
    dateRange: 'current_month',
    customStartDate: '',
    customEndDate: '',
  });
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchSalaries(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchSalaries(currentPage);
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles?all=true');
      setRoles(response.data.data || []);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  };

  const handleDownloadSalarySlip = async (salary) => {
    try {
      const [monthName, year] = salary.month_year.split(' ');
      const month = monthName.toLowerCase();
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = user.token;

      if (!token) {
        Alert.error('Authentication required. Please login again.');
        return;
      }

      const downloadUrl = `${axiosInstance.defaults.baseURL}/salaries/download/slip?user_id=${salary.user_id}&month=${month}&year=${year}`;
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download salary slip');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `salary_slip_${salary.user_id}_${month}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      Alert.success('Salary slip downloaded successfully!');
    } catch (err) {
      Alert.error(err.message || 'Failed to download salary slip');
      console.error('Download error:', err);
    }
  };

  const getDateRange = () => {
    const currentDate = new Date();
    let startDate;
    let endDate;

    switch (filterValues.dateRange) {
      case 'current_month':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = currentDate;
        break;
      case 'last_month':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        endDate = currentDate;
        break;
      case 'last_6_months':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 6, 1);
        endDate = currentDate;
        break;
      case 'last_year':
        startDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1);
        endDate = currentDate;
        break;
      case 'all':
        return { startDate: null, endDate: null };
      case 'custom':
        if (filterValues.customStartDate && filterValues.customEndDate) {
          return {
            startDate: new Date(filterValues.customStartDate),
            endDate: new Date(filterValues.customEndDate),
          };
        }
        return { startDate: null, endDate: null };
      default:
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = currentDate;
    }

    return { startDate, endDate };
  };

  const fetchSalaries = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.user_id) filters.user_id = filterValues.user_id;

      const { startDate, endDate } = getDateRange();

      const response = await axiosInstance.get('/salaries', {
        params: {
          page,
          per_page: perPage,
          ...filters,
          created_after: startDate ? startDate.toISOString() : undefined,
          created_before: endDate ? endDate.toISOString() : undefined,
        },
      });

      setSalaries(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load salary records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (salaryId, userName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete salary record for "${userName}"?`,
      'Delete Salary Record'
    );

    if (confirmed) {
      try {
        await salaryService.delete(salaryId);
        Alert.success('Salary record deleted successfully!');

        if (salaries.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchSalaries();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete salary record');
      }
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.role_name : 'N/A';
  };

  const getDateRangeLabel = () => {
    const labels = {
      current_month: 'Current Month',
      last_month: 'Last Month',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last Year',
      all: 'All Time',
      custom: 'Custom Range',
    };
    return labels[filterValues.dateRange] || 'Current Month';
  };

  const clearFilters = () => {
    setFilterValues({
      user_id: '',
      status: '',
      dateRange: 'current_month',
      customStartDate: '',
      customEndDate: '',
    });
    setCurrentPage(1);
  };

  const filterConfig = [
    ...(!isAgentOrDriver
      ? [
          {
            key: 'user_id',
            label: 'User',
            type: 'select',
            options: users.map((user) => ({
              value: user.user_id,
              label: `${user.name} (${getRoleName(user.role_id)}) - ${user.user_id}`,
            })),
          },
        ]
      : []),
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'credited', label: 'Credited' },
        { value: 'processing', label: 'Processing' },
        { value: 'hold', label: 'Hold' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'select',
      options: [
        { value: 'current_month', label: 'Current Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'last_3_months', label: 'Last 3 Months' },
        { value: 'last_6_months', label: 'Last 6 Months' },
        { value: 'last_year', label: 'Last Year' },
        { value: 'all', label: 'All Time' },
        { value: 'custom', label: 'Custom Range' },
      ],
    },
  ];

  const columns = [
    {
      key: 'salary_id',
      label: 'Salary ID',
      render: (salary) => (
        <button
          type="button"
          onClick={() => navigate(`/salaries/view/${salary.salary_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {salary.salary_id}
        </button>
      ),
    },
    {
      key: 'user',
      label: 'User',
      render: (salary) => (
        <div className="text-sm">
          <div className="font-medium">{salary.user_details?.name}</div>
          <div className="text-xs text-gray-500">
            {getRoleName(salary.user_details?.role_id)} • {salary.user_details?.user_id}
          </div>
          <div className="text-xs text-gray-400">{salary.user_details?.city || 'N/A'}</div>
        </div>
      ),
    },
    { key: 'month_year', label: 'Period' },
    {
      key: 'basic_salary',
      label: 'Basic Salary',
      render: (salary) => formatCurrency(salary.basic_salary),
    },
    {
      key: 'deductions',
      label: 'Deductions',
      cellClassName: 'text-red-600 font-medium',
      render: (salary) => formatCurrency(salary.deductions),
    },
    {
      key: 'net_salary',
      label: 'Net Salary',
      cellClassName: 'text-emerald-600 font-semibold',
      render: (salary) => formatCurrency(salary.net_salary),
    },
    {
      key: 'status',
      label: 'Status',
      render: (salary) => <StatusBadge status={salary.status} />,
    },
  ];

  const getActions = (salary) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/salaries/view/${salary.salary_id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/salaries/edit/${salary.salary_id}`),
    },
    {
      label: 'Download Slip',
      icon: Download,
      onClick: () => handleDownloadSalarySlip(salary),
    },
  ];

  return (
    <PageLayout
      title="Salary Records"
      icon={Wallet}
      actions={
        isSystemAdminOrAdmin ? (
          <Button icon={Plus} onClick={() => navigate('/salaries/add')}>
            Add Salary
          </Button>
        ) : null
      }
    >
      <FilterPanel
        filters={filterConfig}
        values={filterValues}
        onChange={setFilterValues}
        onClear={clearFilters}
      />

      {filterValues.dateRange === 'custom' && (
        <div className="bg-white p-4 rounded-card shadow-card mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Start Date</label>
            <input
              type="date"
              value={filterValues.customStartDate}
              onChange={(e) => setFilterValues((prev) => ({ ...prev, customStartDate: e.target.value }))}
              className="input-clean text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">End Date</label>
            <input
              type="date"
              value={filterValues.customEndDate}
              onChange={(e) => setFilterValues((prev) => ({ ...prev, customEndDate: e.target.value }))}
              className="input-clean text-sm"
            />
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={salaries}
        loading={loading}
        emptyMessage={`No salary records found for ${getDateRangeLabel()}`}
        rowKey="salary_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default SalaryList;
